// dtmBase.ts — where the baked terrain lives, for everything that fetches it.
//
// The Atlas and the plot preview descend into the same assets, so the
// region-vs-tile rule lives here once rather than once per caller.

// import.meta.env is undefined outside Vite (plain Node, vitest's node
// environment); the literal `import.meta.env` token stays so Vite's static
// replacement still fires in dev and build.
const env = (import.meta.env ?? {}) as Partial<ImportMetaEnv>;

/**
 * Where the baked terrain lives.
 *
 * Set VITE_DTM_BASE to point at a bucket or CDN and nothing in the engine
 * changes: the tiles are a cache, not source. Every meta.json records the PDS
 * product and the parameters it was cut with, and tools/dtm/rebake.py will
 * regenerate any of it byte-for-byte.
 */
export const DTM_BASE = env.VITE_DTM_BASE ?? '/art/atlas/dtm';

/**
 * Where the per-plot DETAIL tiles live, which may not be where the regions do.
 *
 * A region is ~6.5 MB and every clone wants one; a detail site closes to
 * 45 MB per covering L7 tile and the global pyramid runs into the tens of GB,
 * and both are only touched by a player who descends. So the regions stay in
 * the repo and the tiles move to object storage, and this is the seam between
 * them. Defaults to the region base, which keeps a purely local checkout working.
 */
export const DTM_TILE_BASE = env.VITE_DTM_TILE_BASE ?? DTM_BASE;

/** A tile is addressed `<site>/tiles/<id>`; anything else is a region. */
export function isTilePath(slugOrUrl: string): boolean {
  return slugOrUrl.includes('/tiles/');
}

/** URL for a slug, against whichever base that kind of asset lives under. */
export function dtmUrl(slug: string): string {
  return `${isTilePath(slug) ? DTM_TILE_BASE : DTM_BASE}/${slug}`;
}
